import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import { BottomSheet } from '@/components/BottomSheet';
import { Button } from '@/components/primitives/Button';
import { Icon } from '@/components/Icon';
import { StatusPill } from '@/components/primitives/StatusPill';
import { Spinner } from '@/components/primitives/Spinner';
import { useBackup, useDownloadBackup, usePurgeBackup, } from '@/api/useBackups';
import { formatBytes } from '@/lib/bytes';
import { relativeTime } from '@/lib/relativeTime';
import s from './BackupDetailSheet.module.css';
function tone(status) {
    if (status === 'running')
        return 'running';
    if (status === 'succeeded')
        return 'ok';
    if (status === 'partial')
        return 'warn';
    if (status === 'failed')
        return 'fail';
    if (status === 'soft_deleted')
        return 'deleted';
    return 'paused';
}
function formatAbs(iso) {
    if (!iso)
        return '—';
    try {
        return new Date(iso).toLocaleString();
    }
    catch {
        return iso;
    }
}
function fmtDuration(start, end) {
    if (!end)
        return '—';
    const ms = new Date(end).getTime() - new Date(start).getTime();
    if (!isFinite(ms) || ms < 0)
        return '—';
    if (ms < 1000)
        return `${ms}ms`;
    const sec = Math.round(ms / 1000);
    if (sec < 60)
        return `${sec}s`;
    const m = Math.floor(sec / 60);
    const rs = sec % 60;
    return rs ? `${m}m ${rs}s` : `${m}m`;
}
function locationTone(status) {
    if (status === 'uploaded')
        return 'ok';
    if (status === 'pending' || status === 'pending_delete')
        return 'queued';
    if (status === 'failed')
        return 'fail';
    if (status === 'deleted')
        return 'deleted';
    return 'paused';
}
/**
 * Inline click-to-copy text. Whole element acts as a button — click anywhere
 * on the text copies it to clipboard and shows a brief "copied" state.
 */
function Copyable({ value, className, children, }) {
    const [copied, setCopied] = useState(false);
    const onClick = () => {
        navigator.clipboard?.writeText(value).then(() => {
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1200);
        }, () => { });
    };
    return (_jsxs("span", { role: "button", tabIndex: 0, onClick: onClick, onKeyDown: (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
            }
        }, title: copied ? 'Copied' : 'Click to copy', "data-copied": copied || undefined, className: `${s.copyable} ${className ?? ''}`, children: [children ?? value, copied && _jsx("span", { className: s.copiedHint, children: "copied" })] }));
}
export function BackupDetailSheet({ backupId, onClose, onRestore, }) {
    const q = useBackup(backupId);
    const dl = useDownloadBackup();
    const purge = usePurgeBackup();
    const b = q.data;
    const status = b?.status ?? 'unknown';
    const dbName = b?.database_name ?? '—';
    const subtitle = b ? `${formatAbs(b.started_at)} · ${b.id.slice(0, 8)}` : 'Loading…';
    const onDownload = () => {
        if (!b)
            return;
        dl.mutate(b.id, {
            onSuccess: (data) => {
                window.location.href = data.download_url;
            },
        });
    };
    const onPurge = () => {
        if (!b)
            return;
        if (!confirm(`Delete this backup of ${dbName}? Data in storage will be removed on next retention sweep. This cannot be undone.`))
            return;
        purge.mutate(b.id, { onSuccess: onClose });
    };
    const dlDisabled = !b ||
        dl.isPending ||
        status === 'failed' ||
        status === 'purged' ||
        status === 'soft_deleted';
    const restoreDisabled = !b || status === 'failed' || status === 'purged';
    const purgeDisabled = !b || status === 'purged' || purge.isPending;
    return (_jsxs(BottomSheet, { open: true, title: _jsxs("div", { className: s.statusBar, children: [_jsxs("span", { children: ["Backup of ", dbName] }), b && _jsx(StatusPill, { tone: tone(status), children: status })] }), subtitle: _jsx("span", { className: s.headSubtitle, children: subtitle }), onClose: onClose, footer: _jsxs("div", { className: s.footActions, children: [_jsx(Button, { variant: "ghost", onClick: onClose, children: "Close" }), _jsxs("div", { className: s.footRight, children: [_jsxs(Button, { onClick: onDownload, disabled: dlDisabled, children: [_jsx(Icon, { name: "download", size: 14 }), " Download"] }), _jsxs(Button, { variant: "ghost", onClick: () => b && onRestore(b.id), disabled: restoreDisabled, children: [_jsx(Icon, { name: "restore", size: 14 }), " Restore to new DB"] }), _jsxs(Button, { variant: "danger", onClick: onPurge, disabled: purgeDisabled, children: [_jsx(Icon, { name: "trash", size: 14 }), " Delete"] })] })] }), children: [q.isLoading && (_jsxs("div", { className: s.placeholder, children: [_jsx(Spinner, { size: 14 }), " Loading backup details\u2026"] })), q.error && (_jsx("div", { className: s.errBlock, children: q.error.message ?? 'Failed to load' })), b && (_jsxs(_Fragment, { children: [status === 'failed' && b.error_message && (_jsx("div", { className: s.errBlock, children: b.error_message })), status === 'partial' && (_jsx("div", { className: s.warnBlock, children: "Backup uploaded to some storages, but not all. See locations below for details." })), _jsxs("div", { className: s.section, children: [_jsx("div", { className: s.sectionHead, children: "Overview" }), _jsxs("div", { className: s.kvRow, children: [_jsx("span", { className: s.kvKey, children: "Database" }), _jsx("span", { className: s.kvValue, children: dbName })] }), _jsxs("div", { className: s.kvRow, children: [_jsx("span", { className: s.kvKey, children: "Started" }), _jsxs("span", { className: s.kvValue, children: [formatAbs(b.started_at), ' ', _jsxs("span", { style: { color: 'var(--text-tertiary)' }, children: ["\u00B7 ", relativeTime(b.started_at)] })] })] }), _jsxs("div", { className: s.kvRow, children: [_jsx("span", { className: s.kvKey, children: "Finished" }), _jsxs("span", { className: s.kvValue, children: [formatAbs(b.finished_at), b.finished_at && (_jsxs("span", { style: { color: 'var(--text-tertiary)' }, children: [' ', "\u00B7 ", relativeTime(b.finished_at)] }))] })] }), _jsxs("div", { className: s.kvRow, children: [_jsx("span", { className: s.kvKey, children: "Duration" }), _jsx("span", { className: s.kvValue, children: fmtDuration(b.started_at, b.finished_at) })] }), _jsxs("div", { className: s.kvRow, children: [_jsx("span", { className: s.kvKey, children: "Trigger" }), _jsx("span", { className: s.kvValue, children: b.triggered_by ?? '—' })] }), _jsxs("div", { className: s.kvRow, children: [_jsx("span", { className: s.kvKey, children: "Source size" }), _jsx("span", { className: s.kvValue, children: formatBytes(b.source_size_bytes ?? null) })] }), _jsxs("div", { className: s.kvRow, children: [_jsx("span", { className: s.kvKey, children: "Ciphertext size" }), _jsx("span", { className: s.kvValue, children: formatBytes(b.ciphertext_size_bytes ?? null) })] }), b.soft_deleted_at && (_jsxs("div", { className: s.kvRow, children: [_jsx("span", { className: s.kvKey, children: "Soft-deleted" }), _jsx("span", { className: s.kvValue, children: formatAbs(b.soft_deleted_at) })] }))] }), _jsxs("div", { className: s.section, children: [_jsx("div", { className: s.sectionHead, children: "Integrity" }), _jsxs("div", { className: s.kvRow, children: [_jsx("span", { className: s.kvKey, children: "SHA-256" }), b.sha256 ? (_jsx(Copyable, { value: b.sha256, className: `${s.kvValue} ${s.kvMono}` })) : (_jsx("span", { className: s.kvValue, children: "\u2014" }))] }), _jsxs("div", { className: s.kvRow, children: [_jsx("span", { className: s.kvKey, children: "Backup ID" }), _jsx(Copyable, { value: b.id, className: `${s.kvValue} ${s.kvMono}` })] })] }), _jsxs("div", { className: s.section, children: [_jsxs("div", { className: s.sectionHead, children: ["Storage locations (", b.locations.length, ")"] }), b.locations.length === 0 && (_jsx("div", { className: s.placeholder, children: "No locations recorded." })), b.locations.map((loc) => (_jsxs("div", { className: s.locCard, children: [_jsxs("div", { className: s.locHead, children: [_jsx("span", { className: s.locName, children: loc.storage_name ?? loc.storage_id.slice(0, 8) }), _jsx(StatusPill, { tone: locationTone(loc.status), children: loc.status })] }), loc.object_key && (_jsx(Copyable, { value: loc.object_key, className: s.locKey })), loc.uploaded_at && (_jsxs("div", { className: s.locMeta, children: ["uploaded ", relativeTime(loc.uploaded_at)] })), loc.error_message && _jsx("div", { className: s.locErr, children: loc.error_message })] }, loc.storage_id)))] })] }))] }));
}
